import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import ErrorToast from '../../components/ErrorToast';
import DashboardHeader from '../../components/DashboardHeader';
import FlagIcon from '../../components/FlagIcon';
import * as transactionsActions from '../../store/actions/transactions';
import * as recipientsActions from '../../store/actions/recipients';
import * as API from '../../store/sagas/api';
import { DocumentType } from '../../enums';
import {
  buildTransactionTimeline,
  findTransactionByRequestId,
  formatForeignAmount,
  formatRequestReference,
  formatShortDate,
  formatSignedEuroAmount,
  formatTransferMethod,
  getCountryDisplay,
  getDescriptionLine,
  getExchangeRateLabel,
  getPaymentDocumentName,
  getStatusHeroMeta,
  isIncomingTransaction,
  resolveRecipientForTransaction,
} from '../../utils/transactionDetailHelpers';
import TransactionTimeline from '../../components/TransactionTimeline';
import HansePayLoader from '../../components/HansePayLoader';
import { formatAccountDisplay, formatPaymentPurpose, formatTransferDate, getBankLabel } from '../../utils/recipientHelpers';
import { printTransferReceipt, receiptFromTransaction } from '../../utils/transferReceipt';
import { useNumberFormat } from '../../hooks/useNumberFormat';

const CARD_CLASS = 'rounded-2xl border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-sm)]';

function CurrencyFlagBadge({ currencyCode }) {
  if (!currencyCode) return null;
  return (
    <span
      className='inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border border-[var(--color-border)] bg-[var(--off)] align-[-2px]'
      aria-hidden='true'
    >
      <FlagIcon currencyCode={currencyCode} className='h-2.5 w-3.5 rounded-sm' />
    </span>
  );
}

CurrencyFlagBadge.propTypes = {
  currencyCode: PropTypes.string,
};

CurrencyFlagBadge.defaultProps = {
  currencyCode: '',
};

function DetailSection({ title, tinted, children }) {
  return (
    <div
      className={`border-t border-[var(--color-border)] px-6 pb-[15px] pt-[13px] first:border-t-0 ${
        tinted ? 'bg-gradient-to-b from-[rgba(11,25,41,0.024)] to-transparent' : 'bg-transparent'
      }`}
    >
      <div className='mb-1 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)]'>{title}</div>
      {children}
    </div>
  );
}

function DetailRow({ label, value, mono, emphasis }) {
  return (
    <div className='flex items-center justify-between gap-6 py-[7px]'>
      <span className='shrink-0 text-[12.5px] font-medium text-[var(--text-tertiary)]'>{label}</span>
      <span
        className={`max-w-[70%] break-words text-right text-[var(--n900)] ${
          emphasis ? 'text-[14px] font-semibold' : 'text-[13.5px] font-medium'
        } ${mono ? 'font-[family-name:var(--font-ui)] tracking-[0.02em]' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function DocumentPill({ fileName, opening, onOpen }) {
  const label = opening ? 'Opening…' : fileName;

  return (
    <button
      type='button'
      onClick={onOpen}
      disabled={opening}
      title={opening ? undefined : `Download ${fileName}`}
      className='inline-flex max-w-full cursor-pointer items-center gap-2 rounded-[7px] border border-[var(--n100)] bg-[var(--n50)] px-[11px] py-[6px] text-[12.5px] font-medium text-[var(--n400)] no-underline transition-[background,border-color] duration-150 hover:border-[var(--n200)] hover:bg-[var(--white)] disabled:cursor-wait disabled:opacity-60 font-[family-name:var(--font-ui)]'
    >
      <svg
        width='13'
        height='13'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.7'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='shrink-0'
        aria-hidden='true'
      >
        <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z' />
        <path d='M14 2v6h6' />
      </svg>
      <span className='min-w-0 truncate underline-offset-2'>{label}</span>
      <svg
        width='13'
        height='13'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='shrink-0 opacity-85'
        aria-hidden='true'
      >
        <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3' />
      </svg>
    </button>
  );
}

function StatusHeroPill({ label, color }) {
  return (
    <span
      className='inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-[11px] font-semibold tracking-[0.04em]'
      style={{ color, background: `color-mix(in oklab, ${color} 10%, transparent)` }}
    >
      <span className='h-[5px] w-[5px] rounded-full' style={{ background: color }} />
      {label}
    </span>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  mono: PropTypes.bool,
  emphasis: PropTypes.bool,
};

DetailRow.defaultProps = {
  mono: false,
  emphasis: false,
};

DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  tinted: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

DetailSection.defaultProps = {
  tinted: false,
};

DocumentPill.propTypes = {
  fileName: PropTypes.string.isRequired,
  opening: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
};

DocumentPill.defaultProps = {
  opening: false,
};

StatusHeroPill.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

function TransactionDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestId } = useParams();
  const { gettingTransactions, getTransactionsError, transactions } = useSelector((state) => state.transactions);
  const { recipients } = useSelector((state) => state.recipients);
  const { currencies: configCurrencies } = useSelector((state) => state.config);
  const { preferences, formatNumber } = useNumberFormat();
  const [openingDocumentFileName, setOpeningDocumentFileName] = useState(null);
  const [documentOpenError, setDocumentOpenError] = useState(null);
  const [receiptError, setReceiptError] = useState(null);

  useEffect(() => {
    dispatch(transactionsActions.getTransactions());
    dispatch(recipientsActions.getRecipients());
  }, [dispatch]);

  const transaction = useMemo(() => findTransactionByRequestId(transactions, requestId), [transactions, requestId]);

  const resolvedRecipient = useMemo(
    () => (transaction ? resolveRecipientForTransaction(transaction, recipients) : null),
    [transaction, recipients],
  );

  const incoming = transaction ? isIncomingTransaction(transaction) : false;
  const statusMeta = transaction ? getStatusHeroMeta(transaction.status) : null;
  const country = resolvedRecipient ? getCountryDisplay(resolvedRecipient, configCurrencies) : null;
  const timeline = transaction ? buildTransactionTimeline(transaction) : [];
  const exchangeRate = transaction ? getExchangeRateLabel(transaction, preferences) : null;

  const handleOpenPaymentDocument = async (fileName) => {
    if (!fileName || openingDocumentFileName) return;
    setDocumentOpenError(null);
    setOpeningDocumentFileName(fileName);
    try {
      const accessUrl = await API.getDocumentAccessUrl(DocumentType.CrossBorder, fileName);
      if (accessUrl) {
        window.open(accessUrl, '_blank', 'noopener,noreferrer');
      } else {
        setDocumentOpenError(new Error('Document could not be opened'));
      }
    } catch (err) {
      setDocumentOpenError(err);
    } finally {
      setOpeningDocumentFileName(null);
    }
  };

  const handleBackToTransactions = () => navigate('/transaction-history');

  if (gettingTransactions && !transaction) {
    return (
      <Layout
        hideHeader
        mainPaddingClassName='px-6 py-9 sm:px-11'
        floatBack={{ onClick: handleBackToTransactions, label: 'Back', ariaLabel: 'Back to transactions' }}
      >
        <div className='flex flex-col gap-7'>
          <DashboardHeader navLabel='Transaction' title='Transaction details' />
          <div className='mx-auto flex w-full max-w-[760px] flex-col gap-5'>
            <HansePayLoader variant='inline' />
          </div>
        </div>
      </Layout>
    );
  }

  if (!transaction) {
    return (
      <Layout
        hideHeader
        mainPaddingClassName='px-6 py-9 sm:px-11'
        floatBack={{ onClick: handleBackToTransactions, label: 'Back', ariaLabel: 'Back to transactions' }}
      >
        <div className='flex flex-col gap-7'>
          <DashboardHeader navLabel='Transaction' title='Transaction details' />
          <div className='mx-auto flex w-full max-w-[760px] flex-col gap-5'>
            <div className={`${CARD_CLASS} px-6 py-12 text-center`}>
              <p className='m-0 text-sm text-[var(--text-secondary)]'>
                {getTransactionsError ? 'Unable to load this transaction.' : 'Transaction not found.'}
              </p>
            </div>
          </div>
        </div>
        <ErrorToast error={getTransactionsError} />
      </Layout>
    );
  }

  const recipientName = resolvedRecipient?.name || '—';
  const quoteCurrency = transaction.quoteCurrency || resolvedRecipient?.currency || '';
  const heroAmount = formatSignedEuroAmount(transaction.amountIn, { incoming, preferences });
  const receiveLine = quoteCurrency
    ? `Recipient received ${formatForeignAmount(transaction.amountOut, quoteCurrency, preferences)}${exchangeRate ? ' · rate locked at send' : ''}`
    : null;
  const reference = transaction.paymentReference?.trim() || formatRequestReference(transaction.requestId);
  const description = getDescriptionLine(transaction);
  const purposeLabel = formatPaymentPurpose(transaction.paymentPurpose) || description;
  const method = formatTransferMethod(resolvedRecipient, country?.code);
  const dateSentLabel = formatTransferDate(transaction.started || transaction.dateAdded);
  const dateSettledLabel = formatShortDate(transaction.finished) || (timeline.some((e) => !e.done) ? 'Pending' : '—');
  const accountDisplay = formatAccountDisplay(resolvedRecipient);
  const bankLabel = getBankLabel(resolvedRecipient);
  const recipientId = resolvedRecipient?.id;
  const sentAmountLabel = formatNumber(Math.abs(Number(transaction.amountIn)) || 0);
  const feeAmount = Number(transaction.totalFee) || 0;
  const feeLabel = formatNumber(feeAmount);
  const totalDebitedLabel = formatNumber((Math.abs(Number(transaction.amountIn)) || 0) + feeAmount);
  const paymentDocumentName = getPaymentDocumentName(transaction);

  const handleDownloadReceipt = () => {
    setReceiptError(null);
    try {
      printTransferReceipt(
        receiptFromTransaction(transaction, {
          recipient: resolvedRecipient,
          countryName: country?.name,
          accountDisplay,
          bankLabel,
          transferMethod: method,
          paymentPurposeLabel: purposeLabel,
          exchangeRate,
        }),
        preferences,
      );
    } catch (err) {
      setReceiptError(err);
    }
  };

  return (
    <Layout
      hideHeader
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={{ onClick: handleBackToTransactions, label: 'Back', ariaLabel: 'Back to transactions' }}
    >
      <div className='flex flex-col gap-7'>
        <DashboardHeader navLabel='Transaction' title='Transaction details' />

        <div className='mx-auto flex w-full max-w-[760px] flex-col gap-5'>
          <div className={`${CARD_CLASS} px-9 pb-8 pt-9 text-center`}>
            <div className='mb-[18px] inline-flex items-center gap-2.5'>
              {statusMeta && <StatusHeroPill label={statusMeta.label} color={statusMeta.color} />}
              <span className='text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>
                {incoming ? 'Incoming transfer' : 'Outgoing transfer'}
              </span>
            </div>

            <div
              className={`mb-3 font-display text-[64px] font-light leading-none tracking-[-0.03em] ${
                incoming ? 'text-[var(--color-success)]' : 'text-[var(--n900)]'
              }`}
            >
              {heroAmount}
            </div>

            {receiveLine && (
              <p className='m-0 mb-2.5 text-[11.5px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{receiveLine}</p>
            )}

            <p className='m-0 text-sm text-[var(--text-secondary)]'>
              {incoming ? 'from' : 'to'}{' '}
              <strong className='font-medium text-[var(--n900)]'>
                <span className='mr-1.5'>
                  <CurrencyFlagBadge currencyCode={quoteCurrency} />
                </span>
                {recipientName}
              </strong>
              {country?.name && country.name !== '—' && (
                <span className='text-[var(--text-tertiary)]'> · {country.name}</span>
              )}
            </p>
          </div>

          <div className={`${CARD_CLASS} overflow-hidden`}>
            <DetailSection title='Sent' tinted>
              <DetailRow label='Date sent' value={dateSentLabel} />
              <DetailRow label='Date settled' value={dateSettledLabel} />
              <DetailRow label='Method' value={method} />
            </DetailSection>

            <DetailSection title='Payment'>
              <DetailRow label='Reference' value={reference} mono />
              <DetailRow label='Purpose' value={purposeLabel} />
              <div className='flex items-center justify-between gap-6 py-[7px]'>
                <span className='shrink-0 text-[12.5px] font-medium text-[var(--text-tertiary)]'>Document</span>
                {paymentDocumentName ? (
                  <DocumentPill
                    fileName={paymentDocumentName}
                    opening={openingDocumentFileName === paymentDocumentName}
                    onOpen={() => handleOpenPaymentDocument(paymentDocumentName)}
                  />
                ) : (
                  <span className='text-[13.5px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>—</span>
                )}
              </div>
            </DetailSection>

            <DetailSection title='Recipient' tinted>
              <DetailRow
                label='Name'
                value={
                  <span className='inline-flex items-center justify-end gap-2'>
                    <CurrencyFlagBadge currencyCode={quoteCurrency} />
                    <span>{recipientName}</span>
                  </span>
                }
              />
              <DetailRow
                label='Country'
                value={
                  <span className='inline-flex items-center justify-end gap-2'>
                    <CurrencyFlagBadge currencyCode={quoteCurrency} />
                    <span>{country?.name || country?.display || '—'}</span>
                  </span>
                }
              />
              <DetailRow label='Account' value={accountDisplay} mono />
              <DetailRow label='Bank' value={bankLabel} />
            </DetailSection>

            <DetailSection title='Fee'>
              <DetailRow label='Amount' value={`${sentAmountLabel} €`} />
              <DetailRow label='HansePay fee' value={`${feeLabel} €`} />
              <DetailRow label='Total debited' value={`${totalDebitedLabel} €`} emphasis />
            </DetailSection>
          </div>

          {timeline.length > 0 && <TransactionTimeline timeline={timeline} />}

          <div className='flex flex-wrap gap-2.5'>
            {recipientId && (
              <button
                type='button'
                onClick={() => navigate(`/recipients/${recipientId}`)}
                className='inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-[18px] py-[11px] text-[13px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'
              >
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <path d='M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z' />
                </svg>
                View recipient
              </button>
            )}
            {recipientId && (
              <button
                type='button'
                onClick={() => navigate(`/send-money/${recipientId}`)}
                className='inline-flex cursor-pointer items-center gap-2 rounded-lg border-0 bg-[var(--n900)] px-[18px] py-[11px] text-[13px] font-medium text-white shadow-[var(--shadow-cta)] font-[family-name:var(--font-ui)]'
              >
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <path d='M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z' />
                </svg>
                Send again
              </button>
            )}
            <button
              type='button'
              onClick={handleDownloadReceipt}
              className='ml-auto inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-transparent px-[18px] py-[11px] text-[13px] font-medium text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'
            >
              <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3' />
              </svg>
              Download receipt
            </button>
            {paymentDocumentName && (
              <button
                type='button'
                onClick={() => handleOpenPaymentDocument(paymentDocumentName)}
                disabled={!!openingDocumentFileName}
                className='inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[var(--color-border)] bg-transparent px-[18px] py-[11px] text-[13px] font-medium text-[var(--text-secondary)] disabled:opacity-60 font-[family-name:var(--font-ui)]'
              >
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8' />
                </svg>
                {openingDocumentFileName === paymentDocumentName ? 'Opening…' : 'View invoice'}
              </button>
            )}
          </div>
        </div>
      </div>

      <ErrorToast error={documentOpenError} onDismiss={() => setDocumentOpenError(null)} />
      <ErrorToast error={receiptError} onDismiss={() => setReceiptError(null)} />
    </Layout>
  );
}

export default TransactionDetail;
